import {
  type ComponentProps,
  type ComponentType,
  createElement,
  forwardRef,
} from 'react'
import {
  type ImageStyle,
  StyleSheet,
  type TextStyle,
  type ViewStyle,
} from 'react-native'

import { useTheme } from '~/hooks/use-theme'

type TCSSProperties = ViewStyle | TextStyle | ImageStyle

type TBooleanVariants = {
  true?: unknown
  false?: unknown
}

type TVariantStyleData = Record<string, TCSSProperties>
type TVariants = Record<string, TVariantStyleData>

type TCompoundVariant<V extends TVariants> = {
  variants: Partial<{
    [K in keyof V]: V[K] extends TBooleanVariants ? boolean : keyof V[K]
  }>
  css: TCSSProperties
}

type TStyledProperties<V extends TVariants> = {
  base: TCSSProperties
  variants?: V
  compoundVariants?: TCompoundVariant<V>[]
}

type TStyledPropertiesWithArgs<V extends TVariants> = (
  theme: ReturnType<typeof useTheme>
) => TStyledProperties<V>

export function styled<
  // biome-ignore lint/suspicious/noExplicitAny:
  C extends ComponentType<any>,
  V extends TVariants = Record<string, never>,
>(Comp: C, opts: TStyledProperties<V> | TStyledPropertiesWithArgs<V>) {
  type TVariantProps = V extends Record<string, never>
    ? {}
    : {
        [K in keyof V]?: V[K] extends TBooleanVariants ? boolean : keyof V[K]
      }

  type TComponentProps = ComponentProps<C> & TVariantProps

  // biome-ignore lint/suspicious/noExplicitAny:
  const StyledComponent = forwardRef<any, TComponentProps>((props, ref) => {
    const theme = useTheme()

    const {
      base,
      variants = {} as V,
      compoundVariants = [],
    } = typeof opts === 'function' ? opts(theme) : opts

    const mergedVariantStyles = getMergedVariantStyles<V>(
      props,
      variants,
      compoundVariants
    )

    const styleSheet = createStyleSheet(base, mergedVariantStyles)

    const finalStyles = [...Object.values(styleSheet), props.style].filter(
      Boolean
    )

    return createElement<C>(Comp, {
      ...props,
      ref,
      style: finalStyles,
    })
  })

  StyledComponent.displayName = `Styled${Comp.displayName || Comp.name || 'Component'}`

  return StyledComponent
}

function createStyleSheet(
  base: TCSSProperties,
  mergedVariantStyles: TCSSProperties[]
) {
  return StyleSheet.create({
    base,
    ...mergedVariantStyles.reduce((acc, variantStyle, idx) => {
      const variantKey = `variant-${idx + 1}`
      acc[variantKey] = variantStyle
      return acc
    }, {} as TVariantStyleData),
  })
}

// Função que combina os estilos base com os estilos de variantes e compostos
function getMergedVariantStyles<V extends TVariants>(
  props: Record<string, unknown>,
  variants: V,
  compoundVariants: TCompoundVariant<V>[]
): TCSSProperties[] {
  const mergedVariantStyles: TCSSProperties[] = []

  if (variants && Object.keys(variants).length > 0) {
    addVariantStyles(props, variants, mergedVariantStyles)
  }

  addCompoundVariantStyles(props, compoundVariants, mergedVariantStyles)

  return mergedVariantStyles
}

function addVariantStyles<V extends TVariants>(
  props: Record<string, unknown>,
  variants: V,
  mergedVariantStyles: TCSSProperties[]
) {
  for (const variantKey in variants) {
    if (Object.prototype.hasOwnProperty.call(variants, variantKey)) {
      const variantValue = props[variantKey] as keyof V[typeof variantKey]

      if (variantValue && variants[variantKey]) {
        const variantStyleData = variants[variantKey][variantValue]

        if (variantStyleData) {
          mergedVariantStyles.push(variantStyleData)
        }
      }
    }
  }
}

// Função que adiciona os estilos compostos
function addCompoundVariantStyles<V extends TVariants>(
  props: Record<string, unknown>,
  compoundVariants: TCompoundVariant<V>[],
  mergedVariantStyles: TCSSProperties[]
) {
  for (const compound of compoundVariants) {
    const applyCompound = Object.keys(compound.variants).every(
      variantKey => props[variantKey] === compound.variants[variantKey]
    )

    if (applyCompound) {
      mergedVariantStyles.push(compound.css)
    }
  }
}
